import { Component, computed, effect, inject, Input, OnInit, signal } from '@angular/core';
import { Comment } from '../../../services/comments/models/comment.model';
import { SubmitReplyEvent } from '../edit-comment/edit-comment.component';
import { CommentsFacade } from '../../../state/comments/comments.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { User } from '../../../../models/user';
import { ConfigService } from '../../../../services/config.service';
import { generateId } from '../../../../_core/utils/generate-id';
import { UserFacade } from 'src/app/_core/state/user/user.facade';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss']
})
export class CommentComponent {
  private readonly commentsFacade = inject(CommentsFacade);
  private readonly configService = inject(ConfigService);
  private readonly userFacade = inject(UserFacade);

  @Input() comment: Comment;
  @Input() parentId: string | null = null;
  @Input() recordId: string;

  readonly showReplies = signal<boolean>(false);
  readonly isLoadingCommentsReplies = toSignal(this.commentsFacade.isLoadingReplies$);
  readonly isCreatingCommentsReplies = toSignal(this.commentsFacade.isCreatingReplies$);
  readonly openTextareaId = toSignal(this.commentsFacade.openTextarea$);
  readonly isLoadingReplies = computed(() => this.isLoadingCommentsReplies().includes(this.comment.id));
  readonly isCreatingReplies = computed(() => this.isCreatingCommentsReplies().includes(this.comment.id));
  readonly showReplyButton = computed(() => {
    if (!this.loggedUser()) {
      return false;
    }

    const loggedUserIsOwnerOfComment = this.loggedUser().id === this.comment.user.id;
    return this.loggedUser() && !loggedUserIsOwnerOfComment && this.openTextareaId() !== this.textareaId() && this.configService.get().showReplyComment;
  });
  readonly showDeleteButton = computed(() => {
    if (!this.loggedUser()) {
      return false;
    }

    const loggedUserIsOwnerOfComment = this.loggedUser().id === this.comment.user.id;
    return this.loggedUser() && loggedUserIsOwnerOfComment && this.configService.get().showDeleteComments;
  });
  readonly loggedUser = signal<User | null>(this.userFacade.user);
  readonly showDeleteCommentConfirmModal = signal<boolean>(false);
  readonly textareaId = signal<string>(generateId());

  trackCommentsFn(_, comment: Comment) {
    return comment.id;
  }

  loadMoreReplies(lastEvaluatedKey: string | null) {
    this.commentsFacade.getCommentsByParentId(this.recordId, this.comment.id, lastEvaluatedKey);
  }

  toggleReplies() {
    if (this.showReplies()) {
      this.showReplies.set(false);
      this.commentsFacade.clearRepliesByParentId(this.comment.id);
    } else {
      this.commentsFacade.clearRepliesByParentId(this.comment.id);
      this.showReplies.set(true);
      this.commentsFacade.getCommentsByParentId(this.recordId, this.comment.id);
    }
  }

  showTextarea() {
    this.commentsFacade.openTextarea(this.textareaId());
  }

  hideTextarea() {
    this.commentsFacade.closeTextarea();
  }

  reply(event: SubmitReplyEvent) {
    // When reply to root comment, parentId and rootId are the same id and replyUserId must be null
    // When reply to another reply, parentId and rootId should be different and replyUserId must be filled
    const rootId = this.comment.rootCommentId ?? this.comment.id;
    let replyUserId = null;
    let parentId = rootId;

    if (event.username !== null) {
      replyUserId = this.comment.user.id;
      parentId = this.comment.id;
    }

    this.commentsFacade.postReply(this.recordId, this.loggedUser().id, event.comment, parentId, rootId, replyUserId);
    this.commentsFacade.closeTextarea();
  }

  deleteComment() {
    if (this.comment.rootCommentId !== null) {
      this.commentsFacade.deleteReply(this.comment.id, this.comment.rootCommentId);
    } else {
      this.commentsFacade.deleteComment(this.comment.id);
    }

    this.showDeleteCommentConfirmModal.set(false);
  }

  like() {
    this.commentsFacade.voteComment(this.recordId, this.comment.id, this.loggedUser().id, true, this.comment.rootCommentId);
  }

  dislike() {
    this.commentsFacade.voteComment(this.recordId, this.comment.id, this.loggedUser().id, false, this.comment.rootCommentId);
  }
}
